import React, { useState, useRef } from 'react';
import { Upload, Plus, FileText, Image, Video, Settings, AlertCircle } from 'lucide-react';
import './FileUpload.css';

const FileUpload = ({ onFileUpload }) => {
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef(null);

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        const files = Array.from(e.dataTransfer.files);
        handleFiles(files);
    };

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        handleFiles(files);
    };

    const validateFile = (file) => {
        // Check if file is PDF
        if (file.type !== 'application/pdf') {
            return false;
        }
        
        // Check file size (optional - 50MB limit)
        const maxSize = 50 * 1024 * 1024; // 50MB
        if (file.size > maxSize) {
            return false;
        }
        
        return true;
    };

    const handleFiles = async (files) => {
        if (files.length === 0) return;

        setError('');
        
        // Validate all files first
        const validFiles = [];
        const invalidFiles = [];
        
        for (const file of files) {
            if (validateFile(file)) {
                validFiles.push(file);
            } else {
                invalidFiles.push(file);
            }
        }

        // Show error if any invalid files
        if (invalidFiles.length > 0) {
            setError('Only PDF files are allowed. Please select PDF files only.');
            setTimeout(() => setError(''), 5000);
            return;
        }

        if (validFiles.length === 0) return;

        setUploading(true);
        
        try {
            // Process each valid file
            for (const file of validFiles) {
                console.log('Uploading file:', file.name);
                
                // Call the parent component's callback
                if (onFileUpload) {
                    await onFileUpload(file);
                }
            }
        } catch (error) {
            console.error('Error uploading files:', error);
            setError('Upload failed. Please try again.');
            setTimeout(() => setError(''), 5000);
        } finally {
            setUploading(false);
        }
    };

    const openFileDialog = () => {
        fileInputRef.current?.click();
    };

    return (
        <div className="file-upload-container">
            <div 
                className={`file-upload-area ${isDragOver ? 'drag-over' : ''} ${uploading ? 'uploading' : ''}`}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={openFileDialog}
            >
                <div className="file-upload-content">
                    <div className="file-icons">
                        <div className="folder-icon">
                            <FileText className="main-folder-icon" />
                            {/* <div className="file-type-icons">
                                <Image className="file-type-icon" />
                                <Video className="file-type-icon" />
                                <Settings className="file-type-icon" />
                            </div> */}
                        </div>
                    </div>
                    
                    <div className="upload-text">
                        <h3 className="upload-title">Click to upload</h3>
                        <p className="upload-subtitle">or drag and drop files</p>
                    </div>
                    
                    <p className="upload-description">
                        Drop PDF files and start chatting - for FREE
                    </p>
                    
                    <button 
                        className="add-resources-btn"
                        onClick={(e) => {
                            e.stopPropagation();
                            openFileDialog();
                        }}
                    >
                        <Plus className="plus-icon" />
                        Add Resources
                    </button>
                </div>
            </div>
            
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,application/pdf"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
            />
            
            {/* Error Message */}
            {error && (
                <div className="upload-error">
                    <AlertCircle className="error-icon" />
                    <span>{error}</span>
                </div>
            )}
        </div>
    );
};

export default FileUpload;
